import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, Plus } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import PasswordInput from '@/components/PasswordInput';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

/**
 * Add-User modal for the Admin Panel → Users tab.
 *
 * Two roles use this:
 *   • Super admin — can attach the new user to ANY partner. The `entities`
 *     prop passed in should be the full list.
 *   • Entity admin — can only attach to partners they administer or that
 *     share a business_group with one of theirs. The parent already scopes
 *     the entities it passes (the backend also enforces this — the picker
 *     just avoids showing options that would be rejected).
 *
 * Empty-selection is legal — creates a blank user account you can later
 * attach via the existing "Entities" per-row action.
 */
const AddUserDialog = ({ open, onOpenChange, token, entities = [], onCreated }) => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [entityIds, setEntityIds] = useState(() => new Set());
  const [busy, setBusy] = useState(false);
  const [entityFilter, setEntityFilter] = useState('');

  useEffect(() => {
    if (!open) {
      // Reset only after close so a re-open doesn't flash stale state.
      const t = setTimeout(() => {
        setName('');
        setEmail('');
        setPassword('');
        setEntityIds(new Set());
        setEntityFilter('');
        setBusy(false);
      }, 200);
      return () => clearTimeout(t);
    }
  }, [open]);

  const filteredEntities = useMemo(() => {
    const q = entityFilter.trim().toLowerCase();
    if (!q) return entities;
    return entities.filter(
      (e) =>
        (e.name || '').toLowerCase().includes(q) ||
        (e.business_group || '').toLowerCase().includes(q) ||
        (e.entity_type || '').toLowerCase().includes(q),
    );
  }, [entities, entityFilter]);

  const toggleEntity = (id) => {
    setEntityIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // Basic client-side validation — the backend enforces the same rules, but
  // failing fast keeps the "Create" click cheap.
  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  const canSubmit =
    !busy && name.trim().length > 0 && emailValid && password.length >= 8;

  const submit = async () => {
    if (!canSubmit) return;
    setBusy(true);
    try {
      const res = await axios.post(
        `${API}/admin/users`,
        {
          name: name.trim(),
          email: email.trim().toLowerCase(),
          password,
          entity_ids: Array.from(entityIds),
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const attached = res.data?.entity_ids_attached?.length || 0;
      const skipped = res.data?.entity_ids_skipped?.length || 0;
      toast.success(
        skipped
          ? `User created. Attached to ${attached}, skipped ${skipped} (already have 2 admins).`
          : attached
            ? `User created and attached to ${attached} partner(s).`
            : 'User created',
      );
      onOpenChange(false);
      onCreated?.();
    } catch (err) {
      console.error('Create user error:', err);
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Failed to create user');
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="add-user-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Plus className="h-5 w-5 text-primary" />
            Add User
          </DialogTitle>
          <DialogDescription>
            Create a new account. You can attach them to one or more partners now, or
            leave blank and use the row-level Entities action later.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <Label htmlFor="new-user-name">Name</Label>
            <Input
              id="new-user-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Full name"
              autoComplete="off"
              data-testid="new-user-name-input"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="new-user-email">Email</Label>
            <Input
              id="new-user-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="user@example.com"
              autoComplete="off"
              data-testid="new-user-email-input"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="new-user-password">Temporary password (min 8 chars)</Label>
            <PasswordInput
              id="new-user-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="new-password"
              minLength={8}
              placeholder="Share this with the user securely"
              data-testid="new-user-password-input"
            />
          </div>

          {/* Entity attach — optional. Empty state shows a hint. */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label>Attach to partners (optional)</Label>
              {entityIds.size > 0 && (
                <Badge variant="secondary" data-testid="new-user-entities-count">
                  {entityIds.size} selected
                </Badge>
              )}
            </div>
            {entities.length === 0 ? (
              <p className="text-sm text-muted-foreground italic">
                No partners available to attach.
              </p>
            ) : (
              <>
                <Input
                  value={entityFilter}
                  onChange={(e) => setEntityFilter(e.target.value)}
                  placeholder="Search partners by name, org, or type"
                  className="mb-2"
                  data-testid="new-user-entity-search"
                />
                <div className="max-h-56 overflow-y-auto rounded-md border border-border divide-y divide-border/50">
                  {filteredEntities.length === 0 ? (
                    <p className="text-sm text-muted-foreground italic p-3">
                      No partners match &quot;{entityFilter}&quot;
                    </p>
                  ) : (
                    filteredEntities.map((e) => {
                      const checked = entityIds.has(e.entity_id);
                      return (
                        <label
                          key={e.entity_id}
                          className="flex items-center gap-3 p-2 hover:bg-muted/50 cursor-pointer"
                          data-testid={`new-user-entity-row-${e.entity_id}`}
                        >
                          <Checkbox
                            checked={checked}
                            onCheckedChange={() => toggleEntity(e.entity_id)}
                          />
                          <div className="flex-1 min-w-0">
                            <div className="font-medium truncate">{e.name}</div>
                            <div className="text-xs text-muted-foreground truncate">
                              {e.business_group ? `${e.business_group} · ` : ''}
                              {e.entity_type}
                            </div>
                          </div>
                        </label>
                      );
                    })
                  )}
                </div>
              </>
            )}
          </div>
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={busy}
            data-testid="add-user-cancel"
          >
            Cancel
          </Button>
          <Button onClick={submit} disabled={!canSubmit} data-testid="add-user-submit">
            {busy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
            Create user
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AddUserDialog;
